/**
 * Australian malts — Joe White Maltings, Barrett Burston Malting, Voyager
 * Craft Malt, Mallokup Malt and AMSAT Character Malts.
 *
 * Two houses at opposite ends of the same country. Joe White have malted in
 * Australia since 1858, run maltings from Perth to Tasmania, and are the bags
 * an Australian homebrew shop actually stacks by the door; they are part of the
 * Boortmalt group. Voyager are eleven years old, malt on their own farm at
 * Whitton in the Riverina, and sell single-paddock lots with the grower's name
 * attached.
 *
 * Three more joined in 2026.09.2. Barrett Burston is the other industrial
 * house, national like Joe White. Mallokup (Western Australia) and AMSAT
 * (South Australia) are craft houses like Voyager: `regional`, tagged
 * `craft-malt`, and each block's comment says where its figures came from.
 * The notes below were written for the first two; where a later house departs
 * from them (Mallokup's enzyme figures are °Lintner from their own BeerSmith
 * profile, not °WK), its block says so.
 *
 * ## Units
 *
 * Both publish colour in EBC, so EBC is the figure taken from the maker and the
 * °L beside it is the conversion `°L = (EBC + 1.2) / 2.65` rounded to one
 * decimal, not a second measurement. Both quote diastatic power in
 * Windisch-Kolbach, carried here as `°Lintner = (°WK + 16) / 3.5`. Neither
 * publishes a distilled-water mash pH or titratable buffering — the pH figure
 * on Voyager's sheets is wort pH, which is a different measurement — so `diPh`
 * and `bufferingCapacity` are `null` throughout and the {@link MaltClass}
 * defaults apply.
 *
 * ## How each house publishes
 *
 * **Joe White** put a full analysis on every product page. Colour is a band and
 * the stored figure is its mid-point; extract is a minimum, moisture a maximum
 * and enzymatic activity a minimum, and each of those single bounds is stored
 * as published. The enzymatic row is left blank on every crystal and every
 * roast, where zero is what the process leaves and nothing is being guessed;
 * those rows stay `spec-sheet`. Two rows do not: Dextrin Malt publishes colour
 * only as a ceiling, and Melanoidin Malt is sold as a base malt with no
 * enzymatic figure at all. Both are `representative` and say so. Their stated
 * inclusion rates are the `suggestedMaxPercent` figures here. The Tasmanian
 * Distilling Malt is on their site and is not here: it is sold on predicted
 * spirit yield for distillers, not brewers.
 *
 * **Voyager** publish no per-product page — the whole range lives in one
 * downloadable catalogue — so these rows carry no `url` rather than pointing
 * forty products at one document. Their sheets print two columns, a `SPECS`
 * band and a `TYPICAL` value, and the `TYPICAL` column is what is stored: it is
 * the maker's own statement of what the malt actually assays. They publish no
 * inclusion rates, so every Voyager ceiling here is ours and not theirs.
 *
 * The Organic Veloria page in that catalogue argues with itself — a Vienna
 * product code and a 9–12 EBC colour bar sit under a heading, a prose
 * description and an analysis table that are all Veloria's. The analysis table
 * wins, as it does everywhere else in this package.
 *
 * Where Voyager leave the diastatic row off a malt that plainly has enzyme —
 * their chit malts, the wheats, the ryes, the ancient barleys — the figure here
 * is class-typical, the row is `representative`, and the description says which
 * number was not published.
 */

import type { Fermentable } from '../../schema/fermentable';

/** Australian maltsters, industrial and craft. @see Fermentable */
export const australianFermentables: readonly Fermentable[] = [
  // ---------------------------------------------------------------------
  // Joe White Maltings — Melbourne, malting in Australia since 1858.
  // ---------------------------------------------------------------------
  {
    id: 'joe-white-signature-lager',
    name: 'Signature Lager Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.45,
    diastaticPowerLintner: 90.3,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The tightest specification Joe White publish: at least 81 % extract, a 3.1–3.8 EBC color window barely half a point wide, and 300 WK of enzyme against the 200 their ordinary pilsner malt guarantees. Selected lots from named growers, and the one to reach for when a pale lager has to come out the same twice.',
    tags: ['base', 'lager', 'pilsner', 'australian', 'high-enzyme'],
    url: 'https://www.joewhitemaltings.com.au/malts/signature-lager-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-pilsner',
    name: 'Pilsner Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 61.7,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The house pale base and the bag most Australian homebrewers learn to brew on. Kilned to 3–4 EBC and left clean, with 200 WK minimum — enough to convert itself and a sensible share of adjunct alongside. Whole grist for a lager or a pale ale, and it argues with nothing.',
    tags: ['base', 'pilsner', 'lager', 'australian'],
    url: 'https://www.joewhitemaltings.com.au/malts/pilsner-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-west-coast-pilsner',
    name: 'West Coast Pilsner Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 61.7,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The same pilsner specification malted at Forrestfield in Perth from Western Australian barley rather than eastern-states grain. Joe White sell it as a regional malt and publish an identical analysis, so it substitutes one for one; what changes is the paddock and the freight.',
    tags: ['base', 'pilsner', 'lager', 'australian', 'single-origin'],
    url: 'https://www.joewhitemaltings.com.au/malts/west-coast-pilsner-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-traditional-ale',
    name: 'Traditional Ale Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.7,
    colorEbc: 6,
    diastaticPowerLintner: 61.7,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned a step warmer than the pilsner malt and no further, which buys biscuit and a fuller malt sweetness at 5–7 EBC without spending any of the enzyme. The Australian answer to an English pale ale malt, and the base under most of the country’s ales.',
    tags: ['base', 'ale', 'australian', 'biscuit'],
    url: 'https://www.joewhitemaltings.com.au/malts/traditional-ale-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-vienna',
    name: 'Vienna Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 5,
    colorLovibond: 3.3,
    colorEbc: 7.5,
    diastaticPowerLintner: 47.4,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned past the ale malt into golden-orange, with a nutty aroma and 150 WK still behind it, so it converts itself comfortably. Joe White cap it at 70 % of the grist: enough to build a Vienna lager or a Märzen out of, and a tenth of that already shows.',
    tags: ['kilned', 'vienna', 'australian', 'nutty'],
    url: 'https://www.joewhitemaltings.com.au/malts/vienna-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-light-munich',
    name: 'Light Munich Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 5.5,
    colorEbc: 13.5,
    diastaticPowerLintner: 47.4,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The paler of the two Munich grades at 10–17 EBC, keeping 150 WK and therefore the ability to carry a grist alone. Malty and faintly sweet rather than caramelized, and the usual way to give a helles or a bock depth without reaching for a crystal.',
    tags: ['kilned', 'munich', 'australian', 'malty'],
    url: 'https://www.joewhitemaltings.com.au/malts/light-munich-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-dark-munich',
    name: 'Dark Munich Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 8.6,
    colorEbc: 21.5,
    diastaticPowerLintner: 47.4,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The same 150 WK minimum as the light grade at nearly twice the color, which is unusual and useful: half a grist of it in a dunkel or an Oktoberfest still converts. Robust and bready, and it deepens the malt without adding sweetness.',
    tags: ['kilned', 'munich', 'dark', 'australian', 'malty'],
    url: 'https://www.joewhitemaltings.com.au/malts/dark-munich-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-wheat',
    name: 'Wheat Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5.5,
    colorLovibond: 2.1,
    colorEbc: 4.25,
    diastaticPowerLintner: 84.6,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Premium white wheat malted the way the barley is, at 81 % extract and 280 WK — the most enzymatic malt in the range. Half a grist of it makes a weissbier; five or ten percent anywhere else buys foam and a softer mouthfeel. Husk-free, so plan the runoff.',
    tags: ['base', 'wheat', 'australian', 'head-retention'],
    url: 'https://www.joewhitemaltings.com.au/malts/wheat-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-dextrin',
    name: 'Dextrin Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 7,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A carapils-type malt bought for body, foam and a subtle sweetness rather than for color or flavor, and Joe White hold it to a tenth of the grist. Color is published only as a 5 EBC ceiling rather than a band, and no enzymatic figure is given, so the row is treated as spent and stays representative.',
    tags: ['dextrin', 'australian', 'body', 'head-retention'],
    url: 'https://www.joewhitemaltings.com.au/malts/dextrin-malt',
    provenance: 'representative',
  },
  {
    id: 'joe-white-melanoidin',
    name: 'Melanoidin Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 26.9,
    colorEbc: 70,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Maillard chemistry forced in the kiln, which is how a malt gets bread, honey and a ruby cast without being stewed. Joe White quote 10–60 % of the mash; the ceiling here is a fifth, because they publish no enzymatic figure for it and a grist that leans on it needs conversion from somewhere else.',
    tags: ['kilned', 'melanoidin', 'red', 'australian', 'malty'],
    url: 'https://www.joewhitemaltings.com.au/malts/melanoidin-malt',
    provenance: 'representative',
  },
  {
    id: 'joe-white-caralite',
    name: 'Caralite Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 7,
    colorLovibond: 11.8,
    colorEbc: 30,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest crystal Joe White make, at 25–35 EBC: golden rather than amber, and there for foam, mouthfeel and a light toffee sweetness a pale beer can carry. Their ceiling is a generous 30 %, though five to ten is where it usually lands.',
    tags: ['crystal', 'caramel', 'pale', 'australian', 'body'],
    url: 'https://www.joewhitemaltings.com.au/malts/caralite-malt-base-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-cara',
    name: 'Cara Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 7,
    colorLovibond: 25.0,
    colorEbc: 65,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Stewed from green malt like a crystal but roasted cooler and shorter, which keeps the caramel full and the color at 50–80 EBC. Rounder and less raisiny than the numbered crystals above it, and the grade an amber ale usually wants first.',
    tags: ['crystal', 'caramel', 'australian', 'toffee'],
    url: 'https://www.joewhitemaltings.com.au/malts/cara-malt-base-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-light-crystal',
    name: 'Light Crystal Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 45.7,
    colorEbc: 120,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The first of the three numbered crystals, at 100–140 EBC — light caramel, dried fruit and enough color to pull a beer amber if it is used freely. Rich in reducing sugars, which is where the body and the flavor stability come from.',
    tags: ['crystal', 'caramel', 'amber', 'australian'],
    url: 'https://www.joewhitemaltings.com.au/malts/light-crystal-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-medium-crystal',
    name: 'Medium Crystal Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A narrow grade at 140–160 EBC, sitting between the light and dark crystals and reading as intense dark caramel with a bitter edge behind the sweetness. Amber to red in the glass at five to ten percent.',
    tags: ['crystal', 'caramel', 'red', 'australian', 'toffee'],
    url: 'https://www.joewhitemaltings.com.au/malts/medium-crystal-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-dark-crystal',
    name: 'Dark Crystal Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    colorLovibond: 104.2,
    colorEbc: 275,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The deepest of the crystals at 250–300 EBC, running to treacle and burgundy rather than toffee. Joe White allow a fifth of the grist; two or three percent is enough to set the color of a dark ale and most of its sweetness with it.',
    tags: ['crystal', 'caramel', 'dark', 'australian', 'treacle'],
    url: 'https://www.joewhitemaltings.com.au/malts/dark-crystal-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-amber',
    name: 'Amber Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 72,
    moisturePercent: 5.5,
    colorLovibond: 21.2,
    colorEbc: 55,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Joe White file it with the roasts and it is the palest of them at 40–70 EBC, but what it tastes of is biscuit and toffee rather than coffee — a toasted malt, not a roasted one. Ten percent is their ceiling and it earns the ochre cast on its own.',
    tags: ['kilned', 'amber', 'australian', 'biscuit', 'toffee'],
    url: 'https://www.joewhitemaltings.com.au/malts/amber-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-chocolate',
    name: 'Chocolate Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 5,
    colorLovibond: 245.7,
    colorEbc: 650,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Gently roasted to 500–800 EBC, which is the depth where cocoa and coffee arrive without the ash behind them. A rich red-brown at two or three percent, and Joe White stop at ten.',
    tags: ['roasted', 'chocolate', 'australian', 'cocoa', 'porter'],
    url: 'https://www.joewhitemaltings.com.au/malts/chocolate-malt-14yiv',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-light-roasted',
    name: 'Light Roasted Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 4.5,
    colorLovibond: 340.1,
    colorEbc: 900,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The first rung of a three-step roast ladder, at 800–1000 EBC. Chocolate and coffee with the smooth finish a drum roast gives, and Joe White allow it further than the darker grades at up to 15 % of a stout or a dark ale.',
    tags: ['roasted', 'australian', 'chocolate', 'coffee', 'stout'],
    url: 'https://www.joewhitemaltings.com.au/malts/light-roasted-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-dark-roasted',
    name: 'Dark Roasted Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 4.5,
    colorLovibond: 491.0,
    colorEbc: 1300,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle grade at 1200–1400 EBC, taken far enough for a firm roast note but still short of the bitterness a black malt or roast barley brings. Color first, flavor second, and one to two percent moves a beer a long way.',
    tags: ['roasted', 'dark', 'australian', 'colour-malt', 'stout'],
    url: 'https://www.joewhitemaltings.com.au/malts/dark-roasted-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-black',
    name: 'Black Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 4.5,
    colorLovibond: 566.5,
    colorEbc: 1500,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest thing Joe White roast, at 1400–1600 EBC, and quieter on the nose than it looks: a dry, bitter, burnt-coffee finish with little aroma to go with it. A percent or two colors a stout black without deciding how it tastes.',
    tags: ['roasted', 'black', 'australian', 'colour-malt', 'bitter'],
    url: 'https://www.joewhitemaltings.com.au/malts/black-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 4,
    colorLovibond: 528.8,
    colorEbc: 1400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley taken to 1200–1600 EBC, and sharper than the black malt at the same depth — Joe White’s own word for it is astringent. The red-black cast and the dry espresso bite of an Irish stout; 3–8 % is the working band.',
    tags: ['roasted', 'unmalted', 'australian', 'stout', 'coffee'],
    url: 'https://www.joewhitemaltings.com.au/malts/roasted-barley',
    provenance: 'spec-sheet',
  },
  {
    id: 'joe-white-roasted-wheat',
    name: 'Roasted Wheat Malt',
    maker: 'Joe White Maltings',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 4,
    colorLovibond: 528.8,
    colorEbc: 1400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat roasted to the same 1200–1600 EBC as their roast barley and much milder for it, because there is no husk to turn harsh. Bought for color and body in a stout rather than for flavor, and it leaves the bitterness to something else.',
    tags: ['roasted', 'wheat', 'australian', 'smooth', 'stout'],
    url: 'https://www.joewhitemaltings.com.au/malts/roasted-wheat-malt',
    provenance: 'spec-sheet',
  },

  // ---------------------------------------------------------------------
  // Barrett Burston Malting — Geelong, Perth and Pinkenba, since 1912.
  //
  // The other big Australian house, with the bags an Australian homebrew
  // shop stacks next to Joe White's. Three industrial malthouses (Geelong
  // VIC, Welshpool WA, Pinkenba QLD). The Perth page names United Malt Group
  // as the parent. Every product has its own page on bbmalt.com.au and a
  // spec-sheet PDF co-branded with Cryer Malt, the distributor.
  //
  // What they publish: a colour band in EBC, moisture as a maximum, extract
  // as a minimum and an inclusion rate. They publish **no diastatic power
  // for any brewing malt**; the only WK figure on the site is on the
  // distilling sheets (250 WK min). So every malt here that can convert
  // carries a class-typical °Lintner and is `representative`. The Vienna,
  // Munich, Dark Munich, Amber, Extra Dark Crystal and Roasted Malt sheets
  // print extract (and some moisture) as "N/A" too, and those gaps are
  // class-typical as well. Only Caramalt and Crystal Malt publish every figure
  // stored here, with zero enzyme as a matter of process, so those two are
  // `spec-sheet`. The extract figure is printed without a basis. It is read
  // as fine-grind dry, which is how Joe White print the same number.
  //
  // Pale Malt's HTML page says 3.3–4.5 EBC and its 2025 PDF says 3.5–4.5.
  // The PDF is the spec sheet, so it wins.
  //
  // Left out on purpose:
  // - Distilling Malt and Non-GN Distilling Malt. Both are sold on predicted
  //   spirit yield, which is the Joe White and Bairds exclusion.
  // - Victoria Pale, Border Pale and Border Ale. These are regional pales
  //   malted only at Geelong or Pinkenba, and each prints the same analysis
  //   as Pale or Ale Malt. Their pages and 2020 sheets are still published,
  //   but the range page no longer links them.
  // - Medium Crystal, Dark Crystal, Chocolate Malt, Roasted Barley and Dark
  //   Roasted Malt. Their pages are still live but date from 2021, carry the
  //   2020 sheets, and are no longer on the range page. The current range
  //   has one crystal, one extra-dark crystal and one roast.
  //
  // Victoria Ale Malt is in, even though the range page does not link it. Its
  // page and its 2024 spec sheet are current, and it is the bag KegLand sells
  // as "Victorian Ale Malt". The page heading says "Victoria" and the slug says
  // "victorian"; the row uses the heading.
  // ---------------------------------------------------------------------
  {
    id: 'barrett-burston-australian-extra-pale',
    name: 'Australian Extra Pale Malt',
    maker: 'Barrett Burston Malting',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 1.7,
    colorEbc: 3.4,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest malt in the range, with color capped at 3.4 EBC. It is clean and gently malty, a base for lagers and pale ales that should finish light. Barrett Burston publish no enzyme figure, so the diastatic power is class-typical.',
    tags: ['base', 'pilsner', 'lager', 'australian'],
    url: 'https://bbmalt.com.au/australian-extra-pale-malt/',
    provenance: 'representative',
  },
  {
    id: 'barrett-burston-pale',
    name: 'Pale Malt',
    maker: 'Barrett Burston Malting',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.8,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The house base malt, from Australian two-row. It is light golden, clean and lightly malty with a note of honey. It can make up the whole grist for most styles. The enzyme figure is class-typical because none is published.',
    tags: ['base', 'ale', 'lager', 'australian'],
    url: 'https://bbmalt.com.au/pale-malt/',
    provenance: 'representative',
  },
  {
    id: 'barrett-burston-ale',
    name: 'Ale Malt',
    maker: 'Barrett Burston Malting',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.6,
    colorEbc: 5.75,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale Malt cured at a slightly higher kiln temperature, landing at 4.5–7 EBC with a fuller malt character. It suits fuller-flavored and hoppier ales, and can make up the whole grist. The enzyme figure is class-typical.',
    tags: ['base', 'ale', 'australian'],
    url: 'https://bbmalt.com.au/ale-malt/',
    provenance: 'representative',
  },
  {
    id: 'barrett-burston-victoria-ale',
    name: 'Victoria Ale Malt',
    maker: 'Barrett Burston Malting',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.6,
    colorEbc: 5.75,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The Ale Malt specification malted only at Geelong and sold ex-Victoria. It substitutes one-for-one for Ale Malt, and retailers often list it as Victorian Ale Malt. The enzyme figure is class-typical.',
    tags: ['base', 'ale', 'australian', 'single-origin'],
    url: 'https://bbmalt.com.au/victorian-ale-malt/',
    provenance: 'representative',
  },
  {
    id: 'barrett-burston-wheat',
    name: 'Wheat Malt',
    maker: 'Barrett Burston Malting',
    origin: 'Australia',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 6,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Clean, wheaty and pale, with color capped at 5.5 EBC. A small share adds body and head retention. It can also carry a hefeweizen, gose or Berliner weisse as the main malt. The enzyme figure is class-typical.',
    tags: ['base', 'wheat', 'australian'],
    url: 'https://bbmalt.com.au/wheat-malt/',
    provenance: 'representative',
  },
  {
    id: 'barrett-burston-vienna',
    name: 'Vienna Malt',
    maker: 'Barrett Burston Malting',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79.7,
    moisturePercent: 4.5,
    colorLovibond: 4.2,
    colorEbc: 10,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Gives a light gold to soft orange color and low-to-medium malt sweetness at 8–12 EBC. It is for Vienna lagers and Märzen, or for extra body in a pale ale. Only color is published, so extract, moisture and enzyme are class-typical.',
    tags: ['kilned', 'vienna', 'australian'],
    url: 'https://bbmalt.com.au/vienna-malt/',
    provenance: 'representative',
  },
  {
    id: 'barrett-burston-munich',
    name: 'Munich Malt',
    maker: 'Barrett Burston Malting',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 6.9,
    colorEbc: 17,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The green malt is stewed early in kilning, giving a biscuity, nutty malt at 14–20 EBC. It adds depth to a pale ale, amber or bock without heavy sweetness. Only color is published, so extract, moisture and enzyme are class-typical.',
    tags: ['kilned', 'munich', 'australian', 'malty'],
    url: 'https://bbmalt.com.au/munich-malt/',
    provenance: 'representative',
  },
  {
    id: 'barrett-burston-dark-munich',
    name: 'Dark Munich Malt',
    maker: 'Barrett Burston Malting',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 10.5,
    colorEbc: 26.5,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A deeper, bready Munich at 21–32 EBC for dark lagers, dark ales and stouts. Barrett Burston put its ceiling at the whole grist, but its enzyme is modest. Only color is published, so extract, moisture and enzyme are class-typical.',
    tags: ['kilned', 'munich', 'australian', 'bready'],
    url: 'https://bbmalt.com.au/dark-munich-malt-2/',
    provenance: 'representative',
  },
  {
    id: 'barrett-burston-caramalt',
    name: 'Caramalt',
    maker: 'Barrett Burston Malting',
    origin: 'Australia',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 8,
    colorLovibond: 17.4,
    colorEbc: 45,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A light caramel malt at 30–60 EBC with a toffee-like golden color and sweet caramel flavor. Barrett Burston allow up to a fifth of the grist. It works from golden lagers through to darker ales.',
    tags: ['crystal', 'caramel', 'australian', 'toffee'],
    url: 'https://bbmalt.com.au/caramalt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'barrett-burston-crystal',
    name: 'Crystal Malt',
    maker: 'Barrett Burston Malting',
    origin: 'Australia',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 51.4,
    colorEbc: 135,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The mid crystal at 120–150 EBC, with fuller caramel than the Caramalt and notes of raisin and honey. Use a few percent for body in a pale ale or IPA, or more as a building block in darker beers, up to 10 %.',
    tags: ['crystal', 'caramel', 'australian', 'raisin'],
    url: 'https://bbmalt.com.au/crystal-malt/',
    provenance: 'spec-sheet',
  },
  {
    id: 'barrett-burston-extra-dark-crystal',
    name: 'Extra Dark Crystal',
    maker: 'Barrett Burston Malting',
    origin: 'Australia',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 4.5,
    colorLovibond: 104.2,
    colorEbc: 275,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Intense toffee and caramel running into raisin and sultana at 250–300 EBC, for porters, stouts and dark lagers. Barrett Burston cap it at 5 %. The extract figure is class-typical because the sheet prints N/A.',
    tags: ['crystal', 'dark-crystal', 'australian', 'dried-fruit'],
    url: 'https://bbmalt.com.au/extra-dark-crystal/',
    provenance: 'representative',
  },
  {
    id: 'barrett-burston-amber',
    name: 'Amber Malt',
    maker: 'Barrett Burston Malting',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 72,
    moisturePercent: 5,
    colorLovibond: 25,
    colorEbc: 65,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned malt roasted to 50–80 EBC for a dry, biscuity flavor and reddish-brown color. It suits ESB, milds, sweet stouts and London porter, up to 20 %. The extract figure is class-typical because the sheet prints N/A.',
    tags: ['kilned', 'amber', 'australian', 'biscuit', 'dry'],
    url: 'https://bbmalt.com.au/amber-malt/',
    provenance: 'representative',
  },
  {
    id: 'barrett-burston-roasted-malt',
    name: 'Roasted Malt',
    maker: 'Barrett Burston Malting',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 377.8,
    colorEbc: 1000,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The one roast in the current range, which Barrett Burston say is also sold as porter or chocolate malt. It has chocolate and coffee with a light astringency at 850–1150 EBC, up to 5 % in porters and stouts. Only color is published, so extract and moisture are class-typical.',
    tags: ['roasted', 'chocolate', 'australian', 'coffee', 'stout'],
    url: 'https://bbmalt.com.au/roasted-malt/',
    provenance: 'representative',
  },

  // ---------------------------------------------------------------------
  // Voyager Craft Malt — Whitton, New South Wales. On-farm Riverina
  // malthouse; figures are the TYPICAL column of their 2025 catalogue.
  // ---------------------------------------------------------------------
  {
    id: 'voyager-chit',
    name: 'Chit Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 1.2,
    colorEbc: 1.9,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'High-protein Riverina barley kilned the moment it chits, so the big proteins and beta-glucans a full modification would break down survive — 750 mg/l of wort beta-glucan against a normal malt’s 50. Dosed at ten or fifteen percent for foam and haze stability. Voyager publish no diastatic figure, so that one is class-typical for a chit malt.',
    tags: ['base', 'functional', 'foam', 'protein', 'australian', 'craft-malt'],
    provenance: 'representative',
  },
  {
    id: 'voyager-undermodified-pilsner',
    name: 'Undermodified Pilsner Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 4,
    colorLovibond: 1.5,
    colorEbc: 2.8,
    diastaticPowerLintner: 104.6,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted deliberately short — friability 70 % where their other base malts run 90 — for brewers who want to decoct or step-mash rather than infuse. The enzyme is all still there at 350 WK, and so is the beta-glucan at 300 mg/l, which is the trade being made.',
    tags: ['base', 'pilsner', 'undermodified', 'decoction', 'australian', 'craft-malt'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-dexter',
    name: 'Dexter',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81,
    moisturePercent: 5.5,
    colorLovibond: 2.3,
    colorEbc: 4.8,
    diastaticPowerLintner: 104.6,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A continental-style caramel pilsner malt from regeneratively farmed New World barley, kilned to 3–6 EBC and holding 350 WK — the most enzymatic malt Voyager make, and a base rather than a seasoning despite the dextrin character in the name.',
    tags: ['base', 'pilsner', 'high-enzyme', 'australian', 'craft-malt', 'regenerative'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-atlas',
    name: 'Atlas',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 82,
    moisturePercent: 4,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 90.3,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The workhorse pale lager malt of the range: 82 % extract, 2.5–4 EBC and 300 WK, modified hard for a fast, clean infusion mash and a high apparent attenuation. The malt to build a lager on when the malt is not supposed to be the story.',
    tags: ['base', 'lager', 'pilsner', 'high-extract', 'australian', 'craft-malt'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-pale-compass',
    name: 'Pale Compass',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 4,
    colorLovibond: 2.3,
    colorEbc: 4.8,
    diastaticPowerLintner: 90.3,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Compass barley from family farms around Barellan, kilned further than Atlas for a more assertive malt character at the same 82 % extract and 300 WK. A fuller-bodied pale base that will still carry a whole grist, for beer or for spirit.',
    tags: ['base', 'ale', 'pale', 'australian', 'craft-malt', 'single-origin'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-schooner-pale',
    name: 'Schooner Pale',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 90.3,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Schooner is an old Australian variety Voyager’s growers still plant, and it ferments low — apparent attenuation limit 79 % where the New World malts reach 82. The residual sugar is the point: a soft mouthfeel and a honey-vanilla note under a Kölsch or a pale lager.',
    tags: ['base', 'pale', 'heritage', 'australian', 'craft-malt', 'honey'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-veloria',
    name: 'Veloria',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 3.1,
    colorEbc: 7,
    diastaticPowerLintner: 90.3,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The malt Voyager started with, and the one that won World’s Best Ale Malt in 2022: Schooner barley kilned to 6–8 EBC into something that behaves like a traditional English ale malt. Low attenuation, full flavor, and built to be the only malt in a golden or summer ale.',
    tags: ['base', 'ale', 'heritage', 'australian', 'craft-malt', 'award'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-vienna',
    name: 'Vienna',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81,
    moisturePercent: 3.5,
    colorLovibond: 4.6,
    colorEbc: 11,
    diastaticPowerLintner: 61.7,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'High final cure temperatures take it to 9–12 EBC and a bright orange-gold, with toast and biscuit over the top. It still holds 200 WK, so it can be the whole grist of a malt-forward beer rather than a tenth of one.',
    tags: ['kilned', 'vienna', 'australian', 'craft-malt', 'toast'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-munich',
    name: 'Munich',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81,
    moisturePercent: 4,
    colorLovibond: 8.4,
    colorEbc: 21,
    diastaticPowerLintner: 53.1,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Warm, humid kilning to 18–22 EBC for the raisin, nut and biscuit a German Munich malt is bought for. At 170 WK it converts itself with room to spare, so a dunkel can be made of nothing else.',
    tags: ['kilned', 'munich', 'australian', 'craft-malt', 'malty'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-sm40',
    name: 'SM40',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81,
    moisturePercent: 4,
    colorLovibond: 15.5,
    colorEbc: 40,
    diastaticPowerLintner: 53.1,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A dark Munich at 35–45 EBC that keeps the same 170 WK as the lighter grade — dark bread crust, brown sugar and pretzel, with enough enzyme left to make up half a grist. Voyager publish no inclusion rate; half is where the enzyme runs out of headroom.',
    tags: ['kilned', 'munich', 'dark', 'australian', 'craft-malt', 'brown-sugar'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-voodoo',
    name: 'Voodoo',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 3,
    colorLovibond: 30.6,
    colorEbc: 80,
    diastaticPowerLintner: 24.6,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Melanoidin precursors built up by recirculating moisture through the early kilning, then a long cure to force them over: Voyager describe the result as Amber, Munich and medium crystal at once. Deeply sweet and aromatic at 70–90 EBC, and unlike a crystal it still assays 70 WK.',
    tags: ['kilned', 'melanoidin', 'australian', 'craft-malt', 'toffee', 'aroma'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-dark-voodoo',
    name: 'Dark Voodoo',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 3,
    colorLovibond: 81.6,
    colorEbc: 215,
    diastaticPowerLintner: 24.6,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Voodoo again, drum-roasted afterwards to 190–240 EBC: molasses, roasted nut and burnt sugar over the toffee that was already there. Voyager call it a dark crystal, but the sheet still shows 70 WK, so it is a kilned malt that was roasted rather than a stewed one.',
    tags: ['kilned', 'melanoidin', 'dark', 'australian', 'craft-malt', 'molasses'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-winter-wheat',
    name: 'Winter Wheat Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 4,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'High-protein hard white wheat grown through the cool Riverina winter, malted to 83 % extract with a nutty, creamy edge the barley malts do not have. Foam and mouthfeel at a few percent, the body of a wheat beer at half a grist. No diastatic figure is published, so that one is class-typical for wheat malt.',
    tags: ['base', 'wheat', 'australian', 'craft-malt', 'head-retention'],
    provenance: 'representative',
  },
  {
    id: 'voyager-heritage-ryecorn',
    name: 'Heritage Ryecorn Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 84,
    moisturePercent: 4,
    colorLovibond: 2.7,
    colorEbc: 6,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A landrace rye rescued from a farm clearing sale at Sandigo and grown back up from a handful of viable seed. Small-grained and intensely peppery, at 84 % extract — distillers took to it first. Husk-free like every rye, and no diastatic power is published, so that figure is class-typical.',
    tags: ['rye', 'base', 'heritage', 'australian', 'craft-malt', 'spicy'],
    provenance: 'representative',
  },
  {
    id: 'voyager-vampire-rye',
    name: 'Vampire Rye Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 85,
    moisturePercent: 4,
    colorLovibond: 3.1,
    colorEbc: 7,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The larger-grained of Voyager’s two ryes and the softer of the two in the glass — spice without the heritage line’s bite — and at 85 % the highest extract in the range. Slick and huskless, so hull the mash. Diastatic power is not published and the figure here is class-typical.',
    tags: ['rye', 'base', 'australian', 'craft-malt', 'spicy', 'high-extract'],
    provenance: 'representative',
  },
  {
    id: 'voyager-malted-oats',
    name: 'Malted Oats',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 60,
    moisturePercent: 4,
    colorLovibond: 2.0,
    colorEbc: 4,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Yarran milling oats malted rather than flaked, for the creamy, velvet mouthfeel oats are bought for plus whatever enzyme the malting puts back. Extract is low by nature at 60 % — the hull yields nothing — and Voyager publish no diastatic figure, so that one is class-typical for oat malt.',
    tags: ['oat', 'adjunct', 'australian', 'craft-malt', 'body', 'hazy'],
    provenance: 'representative',
  },
  {
    id: 'voyager-triticale-robertson',
    name: 'Triticale Robertson',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 85,
    moisturePercent: 4,
    colorLovibond: 2.0,
    colorEbc: 4,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Triticale is the nineteenth-century rye-wheat hybrid, and Voyager malt the Kokoda variety of it to 85 % extract at 2–6 EBC. It reads as wheat with a rye edge and behaves like both — huskless, high in extract, and worth hulling the mash for. No diastatic figure is published, so that one is class-typical.',
    tags: ['triticale', 'base', 'australian', 'craft-malt', 'high-extract'],
    provenance: 'representative',
  },
  {
    id: 'voyager-tibetan-purple',
    name: 'Tibetan Purple Barley Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 4,
    colorLovibond: 2.0,
    colorEbc: 4,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'An ancient Tibetan barley grown out on the farm and malted pale, tasting of almond and earth where a modern two-row tastes of nothing much. Extract is a surprising 82 % for a landrace. Voyager publish no diastatic power, so that figure is class-typical for a pale barley malt — leave conversion capacity elsewhere until you have mashed it once.',
    tags: ['base', 'heritage', 'australian', 'craft-malt', 'nutty'],
    provenance: 'representative',
  },
  {
    id: 'voyager-ethiopian-black',
    name: 'Ethiopian Black Barley Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 2.7,
    colorEbc: 6,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A hull-less six-row Ethiopian landrace traced to 4500 BC, black in the grain and pale in the glass at 4–8 EBC. No husk means no filter bed, so it wants rice hulls or a mash filter. Voyager publish no diastatic power and the figure here is class-typical for a pale barley malt.',
    tags: ['base', 'heritage', 'hulless', 'australian', 'craft-malt'],
    provenance: 'representative',
  },
  {
    id: 'voyager-malted-corn',
    name: 'Malted Corn',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 65,
    moisturePercent: 4,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Yellow dent corn steeped and germinated rather than flaked or ground into grits, which rounds the flavor out into something fuller and sweeter than raw maize. Extract is 65 % and the starch still needs converting by something else — Voyager publish no diastatic figure, and malted maize carries no useful enzyme.',
    tags: ['maize', 'adjunct', 'australian', 'craft-malt', 'light'],
    provenance: 'representative',
  },
  {
    id: 'voyager-biscuit',
    name: 'Biscuit Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81,
    moisturePercent: 3,
    colorLovibond: 12.9,
    colorEbc: 33,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Their own ale malt taken back into the drum at a low temperature for a long time, which develops toast and a semi-sweet golden character without any roast bitterness. Roasted to order and packed fresh, which matters more here than the analysis does.',
    tags: ['kilned', 'biscuit', 'australian', 'craft-malt', 'toast'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-amber',
    name: 'Amber Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81,
    moisturePercent: 3,
    colorLovibond: 23.1,
    colorEbc: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The next stop up the drum from their biscuit malt, at 50–70 EBC: nuttier and drier, still short of anything that reads as roast. Five to fifteen percent gives an ale a baked-crust top note and an ochre cast.',
    tags: ['kilned', 'amber', 'australian', 'craft-malt', 'nutty'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-chocolate',
    name: 'Chocolate Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 81,
    moisturePercent: 3,
    colorLovibond: 415.5,
    colorEbc: 1100,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Drum-roasted to 1000–1200 EBC for cocoa and coffee with a smooth bitterness behind them. Voyager roast in small batches and pack fresh, on the argument that the volatiles in a dark roast are the first thing to go stale.',
    tags: ['roasted', 'chocolate', 'australian', 'craft-malt', 'cocoa'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-roast-barley',
    name: 'Roast Barley',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 74,
    moisturePercent: 3,
    colorLovibond: 453.3,
    colorEbc: 1200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted barley roasted to 1100–1300 EBC, the classic dry and strongly bitter finish of an Irish stout. Extract drops to 74 % where the malted roasts hold 81, which is what roasting raw grain that hard costs.',
    tags: ['roasted', 'unmalted', 'australian', 'craft-malt', 'stout', 'coffee'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-chocolate-wheat',
    name: 'Chocolate Wheat Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 84,
    moisturePercent: 3,
    colorLovibond: 415.5,
    colorEbc: 1100,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Their hard white wheat roasted to chocolate depth, and at 84 % the highest extract of any roast they make — no husk to give up nothing. Smoother than the barley chocolate at the same color, and the way to darken a dunkles weissbier without roughening it.',
    tags: ['roasted', 'wheat', 'australian', 'craft-malt', 'smooth', 'chocolate'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-chocolate-rye',
    name: 'Chocolate Rye Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 84,
    moisturePercent: 3,
    colorLovibond: 302.3,
    colorEbc: 800,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Heritage ryecorn taken to 700–900 EBC, which keeps the grain’s pepper alive under the cocoa where a barley roast would just be dark. A percent or two is a rye beer’s whole personality.',
    tags: ['roasted', 'rye', 'australian', 'craft-malt', 'spicy', 'chocolate'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-roasted-tibetan-purple',
    name: 'Roasted Tibetan Purple Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 80,
    moisturePercent: 3,
    colorLovibond: 453.3,
    colorEbc: 1200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The ancient Tibetan barley taken all the way to 1100–1300 EBC — roast-barley color from a malted grain, so it lands drier than a chocolate malt and softer than raw roast barley. Nobody else roasts this grain.',
    tags: ['roasted', 'heritage', 'australian', 'craft-malt', 'dark'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-mountain-ash-smoke',
    name: 'Mountain Ash Smoke Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4,
    colorLovibond: 3.5,
    colorEbc: 8,
    diastaticPowerLintner: 90.3,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malt smoked over sustainably felled mountain ash, which comes out as bacon with eucalyptus and citrus behind it — nothing like beechwood. Fully enzymatic at 300 WK, so it could be the whole grist; at a published 70 ppm of phenol, half is already a lot. Voyager quote no inclusion rate, so that ceiling is ours.',
    tags: ['smoked', 'eucalyptus', 'australian', 'craft-malt', 'rauchbier', 'phenolic'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-red-gum-smoke',
    name: 'Red Gum Smoke Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 81,
    moisturePercent: 4,
    colorLovibond: 4.2,
    colorEbc: 10,
    diastaticPowerLintner: 90.3,
    suggestedMaxPercent: 30,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Smoked at the start of kilning over reclaimed red gum pulled from the Murrumbidgee beside the maltings: heavy, meaty, riverside-campfire smoke and 90 ppm of phenol, the most assertive malt in the range. Still 300 WK, so the smoke is the only thing it changes. The 30 % ceiling is ours — Voyager publish none.',
    tags: ['smoked', 'eucalyptus', 'australian', 'craft-malt', 'rauchbier', 'phenolic'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-regen-pale',
    name: 'Regen Pale Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82,
    moisturePercent: 4,
    colorLovibond: 2.0,
    colorEbc: 4,
    diastaticPowerLintner: 104.6,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Voyager’s Certified Sustainable line, malted from regeneratively farmed barley off named growers in New South Wales and Victoria. Clean and biscuity at 3–5 EBC with 82 % extract and 350 WK behind it, which is a base malt anyone would take on the numbers alone.',
    tags: ['base', 'pale', 'australian', 'craft-malt', 'regenerative', 'high-enzyme'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-organic-chit',
    name: 'Organic Chit Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 1.0,
    colorEbc: 1.5,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The chit malt made from Certified Organic barley off the Greenwoods’ farm at Coleambally, and a shade paler and lower in extract than the conventional lot. Same job: ten or fifteen percent for foam, body and haze stability. Diastatic power is not published, so that figure is class-typical for a chit malt.',
    tags: ['base', 'functional', 'foam', 'organic', 'australian', 'craft-malt'],
    provenance: 'representative',
  },
  {
    id: 'voyager-organic-pilsner',
    name: 'Organic Pilsner Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 82,
    moisturePercent: 4,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 104.6,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Low-protein Certified Organic barley malted slightly short of full modification, which is the traditional Czech and German way and shows in a 6.0–6.2 wort pH. Pale, crisp, dense-headed, 82 % extract and 350 WK — the organic lager base of the range.',
    tags: ['base', 'pilsner', 'lager', 'organic', 'australian', 'craft-malt'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-organic-schooner-pale',
    name: 'Organic Schooner Pale',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 90.3,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Certified Organic Schooner barley, and the same specification as the conventional lot down to the 79 % attenuation limit that gives it its soft, honeyed finish. Low color and a fresh sweet malt character: a base for Kölsch, pilsner and pale lager.',
    tags: ['base', 'pale', 'heritage', 'organic', 'australian', 'craft-malt'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-organic-veloria',
    name: 'Organic Veloria',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 3.1,
    colorEbc: 7,
    diastaticPowerLintner: 90.3,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The award-winning ale malt made from Certified Organic Schooner grown on the Greenwoods’ regenerative farm. Full-flavored and low-attenuating at 6–8 EBC, and intended the same way the conventional lot is: as the only malt in a golden ale.',
    tags: ['base', 'ale', 'heritage', 'organic', 'australian', 'craft-malt'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-organic-munich',
    name: 'Organic Munich',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4,
    colorLovibond: 8.4,
    colorEbc: 21,
    diastaticPowerLintner: 53.1,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Certified Organic barley kilned warm and wet to 18–22 EBC for toffee, raisin, nut and biscuit, holding 170 WK. Sweet and aromatic enough to build a malt-driven German lager on and still convert the grist.',
    tags: ['kilned', 'munich', 'organic', 'australian', 'craft-malt', 'malty'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-organic-red-wheat',
    name: 'Organic Red Wheat Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 84,
    moisturePercent: 4,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Certified Organic Beaufort red wheat rather than the usual white, which is what makes it taste of nut and earth where a white wheat malt tastes of bread. Low in protein, 84 % extract, and it still does the foam and mouthfeel job. No diastatic figure is published, so that one is class-typical for wheat malt.',
    tags: ['base', 'wheat', 'red-wheat', 'organic', 'australian', 'craft-malt'],
    provenance: 'representative',
  },
  {
    id: 'voyager-organic-biscuit',
    name: 'Organic Biscuit Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81,
    moisturePercent: 3,
    colorLovibond: 12.9,
    colorEbc: 33,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The biscuit malt drum-roasted from Certified Organic ale malt, at the same 25–40 EBC. Semi-sweet, golden and lightly toasted, with the same argument for buying it fresh that applies to everything they roast.',
    tags: ['kilned', 'biscuit', 'organic', 'australian', 'craft-malt', 'toast'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-organic-amber',
    name: 'Organic Amber Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81,
    moisturePercent: 3,
    colorLovibond: 23.1,
    colorEbc: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Certified Organic amber at 50–70 EBC, a step darker and drier than the organic biscuit: nut and baked crust rather than caramel. Up to about 15 % of an ale, and it colors as much as it flavors.',
    tags: ['kilned', 'amber', 'organic', 'australian', 'craft-malt', 'nutty'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-organic-chocolate',
    name: 'Organic Chocolate Malt',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 81,
    moisturePercent: 3,
    colorLovibond: 415.5,
    colorEbc: 1100,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Certified Organic barley roasted to 1000–1200 EBC — slightly sweet, smoothly bitter, and the organic answer to a chocolate malt in a porter or a stout. A specialty roast in an organic range is rare; most organic lines stop at the base malts.',
    tags: ['roasted', 'chocolate', 'organic', 'australian', 'craft-malt', 'cocoa'],
    provenance: 'spec-sheet',
  },
  {
    id: 'voyager-organic-roast-barley',
    name: 'Organic Roast Barley',
    maker: 'Voyager Craft Malt',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 74,
    moisturePercent: 3,
    colorLovibond: 453.3,
    colorEbc: 1200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted Certified Organic barley roasted to 1100–1300 EBC for the dry, hard, coffee bitterness an Irish stout is built on. The 74 % extract is the roast, not the certification — their conventional lot reads the same.',
    tags: ['roasted', 'unmalted', 'organic', 'australian', 'craft-malt', 'stout'],
    provenance: 'spec-sheet',
  },

  // ---------------------------------------------------------------------------
  // Mallokup Malt — Capel, Western Australia
  //
  // Single-origin WA barley from named family farms, malted on site. Every bag
  // carries a batch ID that pulls up a per-paddock certificate of analysis on
  // their site. A founding member of the Independent Malt Association of WA
  // (2025).
  //
  // Two sources, and they are read differently. **Colour** is the EBC band on
  // their products page (current, updated August 2026), stored at its
  // mid-point. **Extract, moisture, diastatic power and the grist ceiling**
  // come from the BeerSmith profile they publish from their COA page (August
  // 2023). That profile's own colour column is not used: it is their EBC band
  // edge divided by 1.97 (Ale 6/1.97, Amber 80/1.97, Roasted Barley
  // 1200/1.97), which is a different conversion from this catalog's and not a
  // second measurement.
  //
  // The rows are `representative`, though a future editor could argue for
  // `spec-sheet`. The profile is the maker's own data, but it reads like a
  // software profile more than an analysis: protein is 10.5 on every malt,
  // coarse/fine difference 1.5 on every malt, and Roasted Barley's moisture is
  // 0, which is a blank and not a measurement (class-typical 4 % used). The
  // enzyme figures are also high for Australian barley: 160 °Lintner on pale,
  // ale and Munich, and 220 on the chit malt. They are stored as published. If
  // a COA ever shows otherwise, the COA wins.
  //
  // No `url`: one products page carries the whole range.
  // ---------------------------------------------------------------------------
  {
    id: 'mallokup-chit',
    name: 'Chit Malt',
    maker: 'Mallokup Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 70,
    moisturePercent: 7,
    colorLovibond: 1.2,
    colorEbc: 2,
    diastaticPowerLintner: 220,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Barely germinated barley at 1.5–2.5 EBC that adds almost no flavor or color. It is dosed at up to 10 % for foam, head retention and body. The 220 °Lintner is the maker’s own profile figure.',
    tags: ['base', 'functional', 'foam', 'australian', 'craft-malt', 'single-origin'],
    provenance: 'representative',
  },
  {
    id: 'mallokup-german-pilsner',
    name: 'German Pilsner Malt',
    maker: 'Mallokup Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 82.6,
    moisturePercent: 5,
    colorLovibond: 1.6,
    colorEbc: 3.15,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted slightly under-modified in the continental way, extra pale at 2.5–3.8 EBC, with good lautering, body and foam. It is the whole grist for a German or Czech pils.',
    tags: ['base', 'pilsner', 'lager', 'australian', 'craft-malt', 'single-origin'],
    provenance: 'representative',
  },
  {
    id: 'mallokup-pale',
    name: 'Pale Malt',
    maker: 'Mallokup Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82.6,
    moisturePercent: 4.5,
    colorLovibond: 2.1,
    colorEbc: 4.25,
    diastaticPowerLintner: 160,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Straw-colored at 3.5–5 EBC, with a sweet malt flavor and a hint of honey. It is a base for lagers, Kölsch and light pale ales that should finish crisp.',
    tags: ['base', 'ale', 'lager', 'australian', 'craft-malt', 'single-origin'],
    provenance: 'representative',
  },
  {
    id: 'mallokup-ale',
    name: 'Ale Malt',
    maker: 'Mallokup Malt',
    origin: 'Australia',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 82.6,
    moisturePercent: 4.5,
    colorLovibond: 2.9,
    colorEbc: 6.5,
    diastaticPowerLintner: 160,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned to a warm gold at 5.5–7.5 EBC, with light toast and biscuit. A full-flavored base for golden ales, pale ales, IPAs and English-leaning bitters.',
    tags: ['base', 'ale', 'australian', 'craft-malt', 'single-origin', 'biscuit'],
    provenance: 'representative',
  },
  {
    id: 'mallokup-vienna',
    name: 'Vienna Malt',
    maker: 'Mallokup Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81,
    moisturePercent: 4,
    colorLovibond: 4.2,
    colorEbc: 10,
    diastaticPowerLintner: 150,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dark gold to copper at 8–12 EBC, with toast, toffee and nut. It can carry a Vienna lager, Märzen or festbier, or add body in small amounts to a pale beer.',
    tags: ['kilned', 'vienna', 'australian', 'craft-malt', 'single-origin'],
    provenance: 'representative',
  },
  {
    id: 'mallokup-munich',
    name: 'Munich Malt',
    maker: 'Mallokup Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 81,
    moisturePercent: 4.5,
    colorLovibond: 6.5,
    colorEbc: 16,
    diastaticPowerLintner: 160,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Rich malt and deep amber color at 14–18 EBC, with toast, nut and caramel. It suits dunkel, red and amber lagers, and IPAs that want more malt behind them.',
    tags: ['kilned', 'munich', 'australian', 'craft-malt', 'single-origin', 'malty'],
    provenance: 'representative',
  },
  {
    id: 'mallokup-biscuit',
    name: 'Biscuit Malt',
    maker: 'Mallokup Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78.3,
    moisturePercent: 4,
    colorLovibond: 13.7,
    colorEbc: 35,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 45,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Gently roasted to 25–45 EBC for a clean toast and biscuit flavor with little color. Mallokup list it with their roasts, but their profile keeps some enzyme, so it is typed here as a kilned malt.',
    tags: ['kilned', 'biscuit', 'australian', 'craft-malt', 'toast'],
    provenance: 'representative',
  },
  {
    id: 'mallokup-amber',
    name: 'Amber Malt',
    maker: 'Mallokup Malt',
    origin: 'Australia',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76.1,
    moisturePercent: 4,
    colorLovibond: 25,
    colorEbc: 65,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 25,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Toasty, with a hint of espresso and deep copper color at 50–80 EBC. It is for amber and brown beers, and for giving a porter its bready middle.',
    tags: ['kilned', 'amber', 'australian', 'craft-malt', 'toast'],
    provenance: 'representative',
  },
  {
    id: 'mallokup-light-chocolate',
    name: 'Light Chocolate Malt',
    maker: 'Mallokup Malt',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 75,
    moisturePercent: 3.5,
    colorLovibond: 340.1,
    colorEbc: 900,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Light coffee, cocoa and a smooth nuttiness at 800–1000 EBC, with only slight bitterness. It suits brown ales, dunkels, porters and stouts.',
    tags: ['roasted', 'chocolate', 'australian', 'craft-malt', 'coffee'],
    provenance: 'representative',
  },
  {
    id: 'mallokup-dark-chocolate',
    name: 'Dark Chocolate Malt',
    maker: 'Mallokup Malt',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 71.7,
    moisturePercent: 3,
    colorLovibond: 453.3,
    colorEbc: 1200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Strong chocolate and espresso with real bitterness at 1100–1300 EBC, for stouts, porters and winter warmers.',
    tags: ['roasted', 'chocolate', 'australian', 'craft-malt', 'espresso', 'stout'],
    provenance: 'representative',
  },
  {
    id: 'mallokup-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Mallokup Malt',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 491,
    colorEbc: 1300,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Intense espresso and bitter dark chocolate at 1200–1400 EBC, for Irish and imperial stouts, and a small share of a porter or dunkel. Moisture is class-typical because the maker’s profile leaves it blank.',
    tags: ['roasted', 'unmalted', 'australian', 'craft-malt', 'stout', 'coffee'],
    provenance: 'representative',
  },

  // ---------------------------------------------------------------------------
  // AMSAT Character Malts — Burton, Adelaide, South Australia
  //
  // A specialty house: crystal, dextrin and roasts from South Australian barley,
  // done in the drum roasters AMSAT have used for oak alternatives for the
  // wine trade. They returned to malt in November 2023. They say plainly that
  // base malt is not what they do.
  //
  // The figures are AMSAT's own data sheets (crop 2022, analysed with Hartwick
  // College). Their site gates the catalogue behind a form, but the same sheets
  // are published by Ellerslie Hop, their national distributor. Moisture is the
  // published maximum, extract the published dry-basis minimum, and colour the
  // mid-point of the EBC band. All five malts carry zero enzyme by process, so
  // every stored figure is theirs and the rows are `spec-sheet`.
  //
  // Only Chocolate Malt has a published inclusion rate (15 %, on AMSAT's own
  // site). Every other ceiling here is ours.
  //
  // AMSAT's site speaks of "a milk chocolate and a dark chocolate malt".
  // Ellerslie sell them as Chocolate Malt (400–500 EBC, milk-chocolate
  // sensory) and Roasted Malt (900–1100 EBC). The rows use the sheet names,
  // and do not assume the two naming schemes line up.
  //
  // No `url`: AMSAT publish one range page, and the per-product pages are
  // the distributor's.
  // ---------------------------------------------------------------------------
  {
    id: 'amsat-caradex',
    name: 'CaraDex',
    maker: 'AMSAT Character Malts',
    origin: 'Australia',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 6,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A dextrin malt made in a drum roaster rather than a kiln, at 3–7 EBC. It adds body, mouthfeel and foam with almost no color or flavor, typically at 5–10 %.',
    tags: ['crystal', 'dextrin', 'foam', 'body', 'australian', 'craft-malt'],
    provenance: 'spec-sheet',
  },
  {
    id: 'amsat-light-crystal',
    name: 'Light Crystal Malt',
    maker: 'AMSAT Character Malts',
    origin: 'Australia',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 6,
    colorLovibond: 28.8,
    colorEbc: 75,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A glassy, fully converted crystal at 50–100 EBC, tasting of honey, golden syrup, caramel and biscuit. Five to ten percent gives a pale ale or bitter its sweetness and color.',
    tags: ['crystal', 'caramel', 'australian', 'craft-malt', 'honey'],
    provenance: 'spec-sheet',
  },
  {
    id: 'amsat-medium-crystal',
    name: 'Medium Crystal Malt',
    maker: 'AMSAT Character Malts',
    origin: 'Australia',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 6,
    colorLovibond: 66.5,
    colorEbc: 175,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A darker crystal at 150–200 EBC, with molasses, treacle, dried fruit and a rye-bread edge. Suited to ambers, reds, brown ales and porters in small amounts.',
    tags: ['crystal', 'caramel', 'australian', 'craft-malt', 'dried-fruit'],
    provenance: 'spec-sheet',
  },
  {
    id: 'amsat-chocolate',
    name: 'Chocolate Malt',
    maker: 'AMSAT Character Malts',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 4,
    colorLovibond: 170.3,
    colorEbc: 450,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A pale malt roasted gently to 400–500 EBC, giving milk chocolate, Ovaltine and light coffee rather than sharp roast. AMSAT suggest up to 15 % in brown ales, porters, dark lagers and stouts.',
    tags: ['roasted', 'chocolate', 'australian', 'craft-malt', 'milk-chocolate'],
    provenance: 'spec-sheet',
  },
  {
    id: 'amsat-roasted',
    name: 'Roasted Malt',
    maker: 'AMSAT Character Malts',
    origin: 'Australia',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 4,
    colorLovibond: 377.8,
    colorEbc: 1000,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A pale malt roasted slowly to 900–1100 EBC and sold as free from bitterness and astringency, with dark biscuit, black tea and chicory. For color and roast in a stout or porter without the bite of roasted barley.',
    tags: ['roasted', 'black', 'australian', 'craft-malt', 'smooth', 'stout'],
    provenance: 'spec-sheet',
  },
];
